export interface Oklch {
  l: number
  c: number
  h: number
}

/** Canonical neutral ramp from globals.css. The lab uses one hue for clean re-tinting. */
export const BASE_RAMP: ReadonlyArray<{ step: string; l: number; c: number }> =
  [
    { step: '50', l: 0.985, c: 0.0013 },
    { step: '100', l: 0.967, c: 0.0027 },
    { step: '200', l: 0.92, c: 0.0053 },
    { step: '300', l: 0.871, c: 0.008 },
    { step: '400', l: 0.705, c: 0.012 },
    { step: '500', l: 0.552, c: 0.016 },
    { step: '600', l: 0.442, c: 0.0147 },
    { step: '700', l: 0.37, c: 0.012 },
    { step: '800', l: 0.274, c: 0.008 },
    { step: '900', l: 0.21, c: 0.0053 },
    { step: '950', l: 0.141, c: 0.004 },
    { step: '1000', l: 0.096, c: 0.0027 },
  ]

export const KEEP = '__keep__' as const

export interface ThemeLabValues {
  rampHue: number
  chromaMul: number
  brand: Oklch
  radius: number
  displayWeight: number
  fontSans: string
  fontHeading: string
}

export const DEFAULTS: ThemeLabValues = {
  rampHue: 286,
  chromaMul: 1,
  brand: { l: 0.51, c: 0.12, h: 210 },
  radius: 0.875,
  displayWeight: 700,
  fontSans: KEEP,
  fontHeading: KEEP,
}

export const FONT_OPTIONS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Default (keep)', value: KEEP },
  {
    label: 'Uncut Sans',
    value: "'uncut sans', ui-sans-serif, system-ui, sans-serif",
  },
  {
    label: 'Geist Sans',
    value: 'var(--font-geist-sans), ui-sans-serif, sans-serif',
  },
  {
    label: 'Manrope',
    value: 'var(--font-manrope), ui-sans-serif, sans-serif',
  },
  {
    label: 'Outfit',
    value: 'var(--font-outfit-src), ui-sans-serif, sans-serif',
  },
  {
    label: 'Plus Jakarta Sans',
    value: 'var(--font-plus-jakarta-sans), ui-sans-serif, sans-serif',
  },
  {
    label: 'Space Grotesk',
    value: 'var(--font-space-grotesk), ui-sans-serif, sans-serif',
  },
  {
    label: 'EB Garamond',
    value: 'var(--font-eb-garamond), ui-serif, serif',
  },
]

const MANAGED_HTML = [
  ...BASE_RAMP.map((s) => `--base-${s.step}`),
  '--brand',
  '--primary-color',
  '--primary',
  '--ring',
  '--radius',
  '--display-weight',
]

/** Applied on body so Theme Lab overrides next/font variables on the same element. */
const MANAGED_BODY = [
  '--font-sans',
  '--font-uncut-sans',
  '--text-family',
  '--font-heading',
]

const OUTFIT_BODY_KEYS = ['--font-outfit', '--font-outfit-src'] as const

function clearOutfitOverrides(body: HTMLElement): void {
  for (const key of OUTFIT_BODY_KEYS) {
    body.style.removeProperty(key)
  }
}

function roundNumber(n: number, decimalPlaces = 4): number {
  return +n.toFixed(decimalPlaces)
}

function formatOklch({ l, c, h }: Oklch): string {
  return `oklch(${roundNumber(l, 3)} ${roundNumber(c)} ${roundNumber(h, 1)})`
}

export function applyTokens(s: ThemeLabValues): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  for (const { step, l, c } of BASE_RAMP) {
    root.style.setProperty(
      `--base-${step}`,
      `oklch(${roundNumber(l, 3)} ${roundNumber(c * s.chromaMul)} ${roundNumber(s.rampHue, 1)})`,
    )
  }

  const brand = formatOklch(s.brand)
  root.style.setProperty('--brand', brand)
  root.style.setProperty('--primary-color', brand)
  root.style.setProperty('--primary', brand)
  root.style.setProperty('--ring', brand)

  root.style.setProperty('--radius', `${roundNumber(s.radius, 3)}rem`)
  root.style.setProperty('--display-weight', String(s.displayWeight))

  const body = document.body
  if (!body) return

  // Never remap Outfit: `font-outfit` must stay the Outfit face even when
  // Theme Lab previews a different UI sans (e.g. Uncut Sans).
  clearOutfitOverrides(body)

  if (s.fontSans !== KEEP) {
    body.style.setProperty('--font-sans', s.fontSans)
    body.style.setProperty('--font-uncut-sans', s.fontSans)
    body.style.setProperty('--text-family', s.fontSans)
  } else {
    for (const key of MANAGED_BODY) {
      if (key !== '--font-heading') {
        body.style.removeProperty(key)
      }
    }
  }

  if (s.fontHeading !== KEEP) {
    body.style.setProperty('--font-heading', s.fontHeading)
  } else {
    body.style.removeProperty('--font-heading')
  }
}

export function clearTokens(): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  for (const key of MANAGED_HTML) {
    root.style.removeProperty(key)
  }

  const body = document.body
  if (body) {
    for (const key of MANAGED_BODY) {
      body.style.removeProperty(key)
    }
    clearOutfitOverrides(body)
  }
}

export function buildThemeBlock(s: ThemeLabValues): string {
  const lines = [
    ...BASE_RAMP.map(
      ({ step, l, c }) =>
        `  --base-${step}: oklch(${roundNumber(l, 3)} ${roundNumber(c * s.chromaMul)} ${roundNumber(s.rampHue, 1)});`,
    ),
    `  --brand: ${formatOklch(s.brand)};`,
    `  --primary-color: ${formatOklch(s.brand)};`,
    `  --primary: ${formatOklch(s.brand)};`,
    `  --ring: ${formatOklch(s.brand)};`,
    `  --radius: ${roundNumber(s.radius, 3)}rem;`,
    `  --display-weight: ${s.displayWeight};`,
  ]

  if (s.fontSans !== KEEP) {
    lines.push(`  --font-sans: ${s.fontSans};`)
    lines.push(`  --font-uncut-sans: ${s.fontSans};`)
    lines.push(`  --text-family: ${s.fontSans};`)
  }

  if (s.fontHeading !== KEEP) {
    lines.push(`  --font-heading: ${s.fontHeading};`)
  }

  return `[data-theme='lab'] {\n${lines.join('\n')}\n}`
}
